/*
 * Design: Gradient Noir – Stripe-inspired dark homepage
 * Sections: Hero, Trust Numbers, Introduction, Process, Advantages, Client Relationship, Services, CTA
 */
import { useState, useRef } from "react";
import { Link, useLocation } from "wouter";
import { useScrollAnimation, useCountUp } from "@/hooks/useScrollAnimation";
import {
  Globe, Search, Megaphone, Share2, Bot, Shield,
  ClipboardList, Code2, TrendingUp, RefreshCw,
  Award, Workflow, Building2, Handshake, Users, Settings2,
  ArrowLeft, Sparkles, Clock, BookOpen, Send, Loader2
} from "lucide-react";
import { useApiData } from "@/hooks/useApiData";
import api from "@/lib/api";
import { getTrackingData } from "@/lib/utm";
import Turnstile from "@/components/Turnstile";
import { formatDate, categories } from "@/data/blogPosts";
import type { BlogPost, Client } from "@shared/types";
import ClientLogo from "@/components/ClientLogo";
import { usePageMeta } from "@/hooks/usePageMeta";
import { ChevronDown } from "lucide-react";

function AnimatedSection({ children, className = "", delay = 0 }: { children: React.ReactNode; className?: string; delay?: number }) {
  const { ref, isVisible } = useScrollAnimation(0.1);
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ${className}`}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? "translateY(0)" : "translateY(30px)",
        transitionDelay: `${delay}ms`,
      }}
    >
      {children}
    </div>
  );
}

function CounterCard({ end, suffix, label }: { end: number; suffix: string; label: string }) {
  const { ref, isVisible } = useScrollAnimation(0.2);
  const count = useCountUp(end, 2000, isVisible);
  return (
    <div ref={ref} className="text-center">
      <div className="text-5xl lg:text-6xl font-bold font-[Rubik] gradient-text mb-2">
        {suffix}{count}+
      </div>
      <div className="text-white/50 text-base">{label}</div>
    </div>
  );
}

const processSteps = [
  { icon: ClipboardList, title: "מחקר ואסטרטגיה", desc: "ניתוח שוק, מתחרים ומילות מפתח לבניית תוכנית פעולה מדויקת." },
  { icon: Code2, title: "בניית אתר", desc: "פיתוח אתר מודרני, מהיר ומותאם למובייל ולקידום." },
  { icon: TrendingUp, title: "קידום אורגני וצמיחה", desc: "יישום אסטרטגיית SEO שמייצרת תנועה אורגנית ולידים." },
  { icon: RefreshCw, title: "אופטימיזציה ושיפור מתמיד", desc: "מעקב, מדידה ושיפור ביצועים באופן שוטף." },
];

const advantages = [
  { icon: Award, text: "20+ שנות ניסיון בדיגיטל" },
  { icon: Workflow, text: "תהליך עבודה מוכח שמייצר תוצאות" },
  { icon: Settings2, text: "התאמה אישית לכל עסק" },
  { icon: Building2, text: "התמקדות בבניית נכסים דיגיטליים יציבים" },
  { icon: Handshake, text: "שילוב בין בניית אתרים לקידום אורגני" },
  { icon: Users, text: "ליווי לקוחות לאורך שנים" },
];

const services = [
  { icon: Globe, title: "בניית אתרים", href: "/services/web-development" },
  { icon: Search, title: "קידום אורגני", href: "/services/seo" },
  { icon: Megaphone, title: "קידום ממומן", href: "/services/paid-advertising" },
  { icon: Share2, title: "ניהול סושיאל", href: "/services/social-media" },
  { icon: Bot, title: "קידום באמצעות AI", href: "/services/ai-marketing" },
  { icon: Shield, title: "ניהול מוניטין", href: "https://upsiteorm.co.il/", external: true },
];

const faqItems = [
  {
    question: "כמה זמן לוקח לראות תוצאות מקידום אתרים אורגני (SEO)?",
    answer: "קידום אורגני הוא ריצה למרחקים ארוכים, לא ספרינט. לרוב, תתחילו לראות תזוזה משמעותית בדירוגים ובכמות הפניות תוך 3 עד 6 חודשים. היתרון הגדול? ברגע שהאתר שלכם מתבסס בראש התוצאות, הוא הופך לנכס שמייצר פניות באופן קבוע, ללא תלות בתקציב פרסום יומי. באפסייט, אנחנו דואגים לשלוח דוחות חודשיים שקופים כדי שתדעו בדיוק איפה אתם עומדים בכל שלב.",
  },
  {
    question: "האם אתם בונים אתרים שמותאמים מראש לקידום בגוגל?",
    answer: "ברור. כל אתר שאנחנו מקימים נבנה עם תשתית טכנית מושלמת: מהירות טעינה גבוהה, התאמה מלאה למובייל, היררכיית כותרות נכונה וחווית משתמש שתגרום לגולשים להישאר. המטרה שלנו היא שהאתר שלכם לא רק ייראה מצוין, אלא גם יתחיל לעבוד עבורכם מהיום הראשון.",
  },
  {
    question: 'מה זה "קידום ב-AI" ואיך זה עוזר לעסק שלי?',
    answer: "עולם הדיגיטל משתנה, ואנחנו משתנים יחד איתו. קידום ב-AI מאפשר לנו לנתח כמויות אדירות של נתונים, לזהות הזדמנויות לביטויי חיפוש שאחרים מפספסים, ולייצר תוכן איכותי ומדויק במהירות. זה לא אומר שהרובוטים מחליפים אותנו, אלא שהם נותנים לנו (ולכם) יתרון טכנולוגי אדיר שמביא לתוצאות מדויקות יותר במינימום זמן.",
  },
  {
    question: "איך אתם מטפלים בניהול מוניטין בגוגל?",
    answer: "המוניטין שלכם הוא הנכס הכי יקר שלכם. אם מופיעות תוצאות שליליות או לא רצויות בחיפוש השם שלכם או של העסק, אנחנו פועלים בדרכים יצירתיות ואתיות כדי לדחוק אותן למטה ולהבליט את הסיפורים החיוביים והמקצועיים שלכם. המטרה היא שכל מי שמחפש אתכם יראה בדיוק את מה שאתם רוצים שהוא יראה – מותג אמין וחזק.",
  },
  {
    question: "למה כדאי לי לעבוד עם סוכנות בוטיק ולא עם חברה גדולה?",
    answer: 'ההבדל הוא ביחס ובעומק. בחברות ענק אתם לעיתים הופכים ל"מספר" במערכת. באפסייט, אנחנו בוחרים את הלקוחות שלנו בקפידה כדי שנוכל להכיר את העסק שלכם לעומק, להיות זמינים לכל שאלה ולבנות אסטרטגיה שתפורה בדיוק למידות שלכם. כשאתם מצליחים אנחנו מצליחים, וזה מה שהופך את השירות שלנו לאישי באמת.',
  },
];

function FAQItem({ question, answer, index }: { question: string; answer: string; index: number }) {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div className="glass-card overflow-hidden transition-all duration-300">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between gap-4 p-6 text-right"
      >
        <h2 className="text-lg font-bold text-white font-[Rubik] leading-snug">{question}</h2>
        <ChevronDown
          className={`w-5 h-5 text-[#66CDAA] shrink-0 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`}
        />
      </button>
      <div
        className={`overflow-hidden transition-all duration-300 ${isOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"}`}
      >
        <div className="px-6 pb-6 text-white/60 leading-relaxed">
          {answer}
        </div>
      </div>
    </div>
  );
}

function HomeContactForm() {
  const [submitting, setSubmitting] = useState(false);
  const [, setLocation] = useLocation();
  const turnstileToken = useRef<string | null>(null);
  const formTs = useRef(Date.now());

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!turnstileToken.current) {
      alert("אנא המתינו לאימות אבטחה.");
      return;
    }
    setSubmitting(true);
    const form = e.target as HTMLFormElement;
    const data = new FormData(form);
    try {
      await api.post("/contact-submissions", {
        name: data.get("name"),
        phone: data.get("phone"),
        email: data.get("email"),
        message: data.get("message"),
        turnstileToken: turnstileToken.current,
        website: data.get("website") || "",
        _formTs: formTs.current,
        ...getTrackingData("home"),
      });
      form.reset();
      setLocation("/thank-you");
    } catch {
      alert("שגיאה בשליחת הטופס. נסו שוב.");
      setSubmitting(false);
    }
  };

  return (
    <div className="glass-card p-8 lg:p-10">
      <form onSubmit={handleSubmit} className="grid md:grid-cols-2 gap-5">
        {/* Honeypot – hidden from humans, bots auto-fill it */}
        <input
          type="text"
          name="website"
          autoComplete="off"
          tabIndex={-1}
          aria-hidden="true"
          style={{ position: "absolute", left: "-9999px", opacity: 0, height: 0 }}
        />
        <div>
          <label className="block text-sm text-white/60 mb-2">שם</label>
          <input
            name="name"
            type="text"
            required
            className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:border-[#66CDAA]/50 focus:ring-1 focus:ring-[#66CDAA]/30 outline-none transition-all"
          />
        </div>
        <div>
          <label className="block text-sm text-white/60 mb-2">טלפון</label>
          <input
            name="phone"
            type="tel"
            required
            className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:border-[#66CDAA]/50 focus:ring-1 focus:ring-[#66CDAA]/30 outline-none transition-all"
            dir="ltr"
          />
        </div>
        <div className="md:col-span-2">
          <label className="block text-sm text-white/60 mb-2">אימייל</label>
          <input
            name="email"
            type="email"
            required
            className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:border-[#66CDAA]/50 focus:ring-1 focus:ring-[#66CDAA]/30 outline-none transition-all"
            dir="ltr"
          />
        </div>
        <div className="md:col-span-2">
          <label className="block text-sm text-white/60 mb-2">הודעה</label>
          <textarea
            name="message"
            rows={4}
            className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:border-[#66CDAA]/50 focus:ring-1 focus:ring-[#66CDAA]/30 outline-none transition-all resize-none"
            placeholder="ספרו לנו על הפרויקט שלכם..."
          />
        </div>
        <div className="md:col-span-2">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              name="privacy_consent"
              required
              className="mt-1 w-4 h-4 rounded border-white/20 bg-white/5 text-[#66CDAA] focus:ring-[#66CDAA]/30 accent-[#66CDAA] shrink-0"
            />
            <span className="text-sm text-white/50">
              אני מסכים/ה לקבלת פניות ודיוור מאפסייט בהתאם ל<a href="/privacy" className="text-[#66CDAA] hover:underline">מדיניות הפרטיות</a>. ניתן לבטל בכל עת.
            </span>
          </label>
        </div>
        <div className="md:col-span-2">
          <Turnstile
            onVerify={(token) => { turnstileToken.current = token; }}
            onExpire={() => { turnstileToken.current = null; }}
          />
        </div>
        <div className="md:col-span-2">
          <button
            type="submit"
            disabled={submitting}
            className="glow-btn w-full text-base py-4 flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
            {submitting ? "שולח..." : "שלחו הודעה"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default function Home() {
  const { data: blogPosts } = useApiData<BlogPost[]>("/blog-posts?limit=3");
  const { data: clientLogos } = useApiData<Client[]>("/clients");

  usePageMeta({
    title: "קידום אתרים: חברה לקידום אורגני, בניית אתרים ושיווק דיגיטלי | upsite",
    description: "רוצים להופיע ראשונים בתוצאות החיפוש? אפסייט בונה עבורכם נכס דיגיטלי מניב עם קידום אורגני מבוסס AI, שקיפות מלאה וליווי אישי. לחצו להזניק את העסק שלכם",
  });

  return (
    <div>
      {/* ═══════════════════════════════════════════════════════════
          HERO SECTION
      ═══════════════════════════════════════════════════════════ */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        {/* Background layers */}
        <div className="absolute inset-0 bg-[#0F172A]" />
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: `url(https://d2xsxph8kpxj0f.cloudfront.net/310519663396493078/EDyimBtawRo3FYhzWUwDs6/hero-bg-Qud6NmNmCgW3qCRcGgLCLk.webp)`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 gradient-mesh-strong" />
        {/* Glow orbs */}
        <div className="absolute top-1/4 right-1/4 w-[500px] h-[500px] bg-[#66CDAA]/15 rounded-full blur-[150px] animate-pulse-glow" />
        <div className="absolute bottom-1/4 left-1/4 w-[400px] h-[400px] bg-[#3DD5B0]/10 rounded-full blur-[120px]" />

        <div className="container relative z-10 pt-32 pb-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Text */}
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#66CDAA]/10 border border-[#66CDAA]/20 mb-8 animate-fade-in-up">
                <Sparkles className="w-4 h-4 text-[#66CDAA]" />
                <span className="text-sm text-[#66CDAA] font-medium">
                  בניית אתרים וקידום אורגני מקצועי
                </span>
              </div>
              <h1
                className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold text-white mb-6 font-[Rubik] leading-[1.1] animate-fade-in-up"
                style={{ animationDelay: "0.1s" }}
              >
                אפסייט
                <br />
                קידום אורגני
                <br />
                <span className="gradient-text">שמביא תוצאות</span>
              </h1>
              <p
                className="text-lg lg:text-xl text-white/60 leading-relaxed mb-10 max-w-xl animate-fade-in-up"
                style={{ animationDelay: "0.2s" }}
              >
                אנחנו בונים נכסים דיגיטליים שמייצרים חשיפה, פניות ומכירות לאורך זמן.
              </p>
              <div
                className="flex flex-wrap gap-4 animate-fade-in-up"
                style={{ animationDelay: "0.3s" }}
              >
                <Link href="/contact" className="glow-btn text-base px-8 py-4">
                  דברו איתנו
                </Link>

              </div>
            </div>

            {/* Dashboard mockup */}
            <div
              className="hidden lg:block animate-fade-in-up"
              style={{ animationDelay: "0.4s" }}
            >
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-br from-[#66CDAA]/20 to-transparent rounded-3xl blur-2xl" />
                <img
                  src="https://d2xsxph8kpxj0f.cloudfront.net/310519663396493078/EDyimBtawRo3FYhzWUwDs6/dashboard-mockup-9MSfJT2WHhhAxxQdrVtTmo.webp"
                  alt="Analytics Dashboard"
                  className="relative rounded-2xl shadow-2xl shadow-black/40 border border-white/10 animate-float"
                  loading="eager"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Bottom fade */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#0F172A] to-transparent" />
      </section>

      {/* ═══════════════════════════════════════════════════════════
          TRUST NUMBERS (ANIMATED)
      ═══════════════════════════════════════════════════════════ */}
      <section className="py-16 lg:py-20 section-dark relative">
        <div className="absolute inset-0 gradient-mesh" />
        <div className="container relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <CounterCard end={20} suffix="" label="שנות פעילות" />
            <CounterCard end={600} suffix="" label="אתרים שנבנו" />
            <CounterCard end={4000} suffix="" label="אתרים שקודמו" />
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════
          CLIENT LOGOS MARQUEE
      ═══════════════════════════════════════════════════════════ */}
      {clientLogos && clientLogos.length > 0 && (() => {
        return (
          <section className="py-6 lg:py-8 section-navy relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-l from-transparent via-[#66CDAA]/20 to-transparent" />
            <div className="group/row overflow-hidden [mask-image:linear-gradient(to_right,transparent,black_10%,black_90%,transparent)]">
              <div className="flex w-max animate-marquee-rtl group-hover/row:[animation-play-state:paused]">
                {[...clientLogos, ...clientLogos, ...clientLogos].map((client, i) => (
                  <div key={`logo-${i}`} className="flex items-center justify-center h-16 w-32 mx-3 md:h-24 md:w-52 md:mx-6 shrink-0">
                    {(client.svgCode || client.logo) && (
                      <ClientLogo client={client} className={`object-contain ${client.svgCode ? "h-10 md:h-16 w-full" : "max-h-10 md:max-h-16 max-w-full"}`} />
                    )}
                  </div>
                ))}
              </div>
            </div>
          </section>
        );
      })()}

      {/* ═══════════════════════════════════════════════════════════
          INTRODUCTION
      ═══════════════════════════════════════════════════════════ */}
      <section className="py-20 lg:py-28 section-navy relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-l from-transparent via-[#66CDAA]/20 to-transparent" />
        <div className="container">
          <AnimatedSection>
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl lg:text-5xl font-bold text-white mb-8 font-[Rubik]">
                אפסייט – <span className="gradient-text">בנייה וקידום אתרים</span>
              </h2>
              <div className="space-y-6 text-lg text-white/60 leading-relaxed">
                <p>
                  מאחורי כל אתר מצליח עומדת אסטרטגיה מדויקת, ניסיון בשטח ושותף שרץ איתכם למרחקים ארוכים. אם יש לכם אתר ותיק שצריך זריקת מרץ ועדכון טכנולוגי, או אם אתם מקימים נוכחות דיגיטלית מאפס – אתם לא צריכים &quot;עוד ספק&quot;.
                </p>
                <p>
                  אתם צריכים צוות שלוקח אחריות על השורה התחתונה שלכם, חושב קדימה ורותם את הכלים הכי מתקדמים שישאירו את המתחרים מאחור.
                </p>
                <p className="text-white/80 font-medium">
                  בסוף, אנחנו לא מודדים הצלחה בקליקים – אלא בצמיחה של העסק שלך.
                </p>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════
          WORK PROCESS
      ═══════════════════════════════════════════════════════════ */}
      <section className="py-20 lg:py-28 section-dark relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-l from-transparent via-[#66CDAA]/20 to-transparent" />
        <div className="absolute bottom-1/3 right-0 w-96 h-96 bg-[#66CDAA]/5 rounded-full blur-[120px]" />
        <div className="container relative z-10">
          <AnimatedSection>
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-5xl font-bold text-white mb-4 font-[Rubik]">
                איך אנחנו עובדים <span className="gradient-text">באפסייט</span>
              </h2>
              <p className="text-white/50 text-lg max-w-xl mx-auto">
                תהליך מוכח שמייצר תוצאות לאורך זמן
              </p>
            </div>
          </AnimatedSection>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {processSteps.map((step, i) => (
              <AnimatedSection key={i} delay={i * 150}>
                <div className="glass-card glass-card-hover p-8 text-center h-full transition-all duration-300 relative group">
                  {/* Step number */}
                  <div className="absolute top-4 right-4 text-6xl font-bold font-[Rubik] text-white/[0.03] group-hover:text-[#66CDAA]/10 transition-colors">
                    {i + 1}
                  </div>
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#66CDAA] to-[#3DD5B0] flex items-center justify-center mx-auto mb-5">
                    <step.icon className="w-7 h-7 text-[#0F172A]" />
                  </div>
                  <h3 className="text-lg font-bold text-white mb-3 font-[Rubik]">
                    {step.title}
                  </h3>
                  <p className="text-sm text-white/50 leading-relaxed">
                    {step.desc}
                  </p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════
          ADVANTAGES
      ═══════════════════════════════════════════════════════════ */}
      <section className="py-20 lg:py-28 section-navy relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-l from-transparent via-[#66CDAA]/20 to-transparent" />
        <div className="container">
          <AnimatedSection>
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-5xl font-bold text-white mb-4 font-[Rubik]">
                למה לעבוד עם <span className="gradient-text">אפסייט</span>
              </h2>
            </div>
          </AnimatedSection>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {advantages.map((adv, i) => (
              <AnimatedSection key={i} delay={i * 100}>
                <div className="glass-card glass-card-hover p-6 transition-all duration-300 flex gap-4 items-center h-[100px]">
                  <div className="w-12 h-12 rounded-xl bg-[#66CDAA]/10 flex items-center justify-center shrink-0">
                    <adv.icon className="w-6 h-6 text-[#66CDAA]" />
                  </div>
                  <p className="text-white/80 text-base font-medium leading-relaxed">
                    {adv.text}
                  </p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════
          CLIENT RELATIONSHIP
      ═══════════════════════════════════════════════════════════ */}
      <section className="py-20 lg:py-28 section-dark relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-l from-transparent via-[#66CDAA]/20 to-transparent" />
        <div className="absolute top-1/2 left-0 w-80 h-80 bg-[#66CDAA]/5 rounded-full blur-[100px]" />
        <div className="container relative z-10">
          <AnimatedSection>
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <h2 className="text-3xl lg:text-5xl font-bold text-white mb-8 font-[Rubik] leading-tight">
                  למה לקוחות נשארים איתנו{" "}
                  <span className="gradient-text">שנים</span>
                </h2>
                <div className="space-y-6 text-lg text-white/60 leading-relaxed">
                  <p>
                    עבורנו, העלייה של האתר לאוויר היא רק נקודת הזינוק. אנחנו מאמינים בשותפות שמבוססת על שלוש מילים: אמון, שקיפות ותוצאות.
                  </p>
                  <p>
                    הלקוחות שלנו נשארים איתנו שנים כי הם רואים את המספרים מדברים: יותר חשיפה בגוגל, יותר פניות איכותיות ויותר מכירות. אנחנו לא נחים על זרי הדפנה – בכל בוקר אנחנו צוללים לנתונים, מבצעים אופטימיזציה ומחפשים את הדרך הבאה לקחת את העסק שלכם צעד אחד קדימה.
                  </p>
                  <p>
                    כשאתם עובדים עם אפסייט, אתם מקבלים צוות שמכיר את ה-DNA של העסק שלכם כאילו היה שלו. נלך איתכם יד ביד עד לתוצאות.
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { num: "20+", label: "שנות ניסיון" },
                  { num: "100%", label: "שביעות רצון" },
                  { num: "600+", label: "פרויקטים" },
                  { num: "∞", label: "יחס אישי ומסור" },
                ].map((stat, i) => (
                  <div
                    key={i}
                    className="glass-card p-6 text-center animate-pulse-glow"
                    style={{ animationDelay: `${i * 0.5}s` }}
                  >
                    <div className="text-3xl font-bold gradient-text font-[Rubik] mb-1">
                      {stat.num}
                    </div>
                    <div className="text-sm text-white/50">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════
          SERVICES
      ═══════════════════════════════════════════════════════════ */}
      <section className="py-20 lg:py-28 section-navy relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-l from-transparent via-[#66CDAA]/20 to-transparent" />
        <div className="absolute bottom-0 right-1/3 w-96 h-96 bg-[#66CDAA]/5 rounded-full blur-[120px]" />
        <div className="container relative z-10">
          <AnimatedSection>
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-5xl font-bold text-white mb-4 font-[Rubik]">
                <span className="gradient-text">השירותים</span> שלנו
              </h2>
              <p className="text-white/50 text-lg max-w-xl mx-auto">
                פתרונות דיגיטליים מקצועיים שמותאמים לעסק שלכם
              </p>
            </div>
          </AnimatedSection>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {services.map((service, i) => {
              const cardContent = (
                <>
                  <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-5 transition-all duration-300 bg-[#66CDAA]/10 group-hover:bg-gradient-to-br group-hover:from-[#66CDAA] group-hover:to-[#3DD5B0] group-hover:shadow-[0_8px_30px_rgba(102,205,170,0.3)]">
                    <service.icon className="w-8 h-8 text-[#66CDAA] group-hover:text-[#0F172A] transition-colors" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3 font-[Rubik] group-hover:text-[#66CDAA] transition-colors">
                    {service.title}
                  </h3>
                  <span className="inline-flex items-center gap-1 text-sm text-[#66CDAA] opacity-0 group-hover:opacity-100 transition-opacity">
                    למידע נוסף
                    <ArrowLeft className="w-3 h-3" />
                  </span>
                </>
              );
              const cardClass = "block glass-card p-8 text-center transition-all duration-300 group hover:-translate-y-2 relative hover:border-[#66CDAA]/30 hover:shadow-[0_20px_60px_rgba(102,205,170,0.12)]";
              return (
                <AnimatedSection key={i} delay={i * 100}>
                  {service.external ? (
                    <a href={service.href} target="_blank" rel="noopener noreferrer" className={cardClass}>
                      {cardContent}
                    </a>
                  ) : (
                    <Link href={service.href} className={cardClass}>
                      {cardContent}
                    </Link>
                  )}
                </AnimatedSection>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════
          LATEST BLOG ARTICLES
      ═══════════════════════════════════════════════════════════ */}
      <section className="py-20 lg:py-28 section-dark relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-l from-transparent via-[#66CDAA]/20 to-transparent" />
        <div className="absolute top-1/3 right-0 w-80 h-80 bg-[#66CDAA]/5 rounded-full blur-[100px]" />
        <div className="container relative z-10">
          <AnimatedSection>
            <div className="flex items-center justify-between mb-12">
              <div>
                <h2 className="text-3xl lg:text-5xl font-bold text-white mb-4 font-[Rubik]">
                  מהבלוג <span className="gradient-text">שלנו</span>
                </h2>
                <p className="text-white/50 text-lg max-w-xl">
                  תובנות, טיפים וידע מקצועי מצוות המומחים של אפסייט
                </p>
              </div>
              <Link
                href="/blog"
                className="hidden md:inline-flex items-center gap-2 px-5 py-2.5 rounded-xl border border-white/10 text-white/60 hover:text-white hover:border-white/20 transition-all text-sm"
              >
                כל המאמרים
                <ArrowLeft className="w-4 h-4" />
              </Link>
            </div>
          </AnimatedSection>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {(blogPosts || []).map((post, i) => (
              <AnimatedSection key={post.slug} delay={i * 100}>
                <Link
                  href={`/blog/${post.slug}`}
                  className="block glass-card overflow-hidden group hover:-translate-y-1.5 transition-all duration-300 hover:border-[#66CDAA]/20 hover:shadow-[0_16px_48px_rgba(102,205,170,0.1)] h-full"
                >
                  <div className="h-44 overflow-hidden relative">
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A]/60 to-transparent" />
                    <div className="absolute bottom-3 right-3">
                      <span className="px-2.5 py-1 rounded-full bg-[#66CDAA]/15 border border-[#66CDAA]/25 text-[11px] text-[#66CDAA] font-semibold backdrop-blur-sm">
                        {categories.find((c) => c.id === post.category)?.label}
                      </span>
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-3 text-xs text-white/40 mb-3">
                      <span>{formatDate(post.date)}</span>
                      <span className="w-1 h-1 rounded-full bg-white/20" />
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {post.readTime} דק׳ קריאה
                      </span>
                    </div>
                    <h3 className="text-lg font-bold text-white mb-2 font-[Rubik] leading-snug group-hover:text-[#66CDAA] transition-colors line-clamp-2">
                      {post.title}
                    </h3>
                    <p className="text-sm text-white/45 leading-relaxed line-clamp-2">
                      {post.excerpt}
                    </p>
                  </div>
                </Link>
              </AnimatedSection>
            ))}
          </div>

          <div className="mt-8 text-center md:hidden">
            <Link
              href="/blog"
              className="inline-flex items-center gap-2 text-[#66CDAA] font-medium"
            >
              כל המאמרים
              <ArrowLeft className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════
          FAQ SECTION
      ═══════════════════════════════════════════════════════════ */}
      <section className="py-20 lg:py-28 section-navy relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-l from-transparent via-[#66CDAA]/20 to-transparent" />
        <div className="container relative z-10">
          <AnimatedSection>
            <div className="text-center mb-12">
              <h2 className="text-3xl lg:text-5xl font-bold text-white mb-4 font-[Rubik]">
                שאלות <span className="gradient-text">נפוצות</span>
              </h2>
              <p className="text-white/50 text-lg max-w-xl mx-auto">
                תשובות לשאלות שאנחנו שומעים הכי הרבה
              </p>
            </div>
          </AnimatedSection>

          <div className="max-w-3xl mx-auto space-y-4">
            {faqItems.map((item, i) => (
              <AnimatedSection key={i} delay={i * 100}>
                <FAQItem question={item.question} answer={item.answer} index={i} />
              </AnimatedSection>
            ))}
          </div>
        </div>

        {/* FAQ Schema markup */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              mainEntity: faqItems.map((item) => ({
                "@type": "Question",
                name: item.question,
                acceptedAnswer: {
                  "@type": "Answer",
                  text: item.answer,
                },
              })),
            }),
          }}
        />
      </section>

      {/* ═══════════════════════════════════════════════════════════
          CTA SECTION
      ═══════════════════════════════════════════════════════════ */}
      <section className="py-20 lg:py-28 relative overflow-hidden">
        <div className="absolute inset-0 gradient-mesh-strong" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#66CDAA]/10 rounded-full blur-[150px]" />
        <div className="container relative z-10">
          <AnimatedSection>
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl lg:text-5xl font-bold text-white mb-6 font-[Rubik]">
                  בואו <span className="gradient-text">נתקדם</span> יחד
                </h2>
                <p className="text-lg text-white/60 max-w-xl mx-auto">
                  רוצים אתר חזק יותר, קידום שמביא תוצאות, או פשוט צוות שאפשר לסמוך עליו לאורך זמן?
                </p>
              </div>

              {/* Contact Form */}
              <HomeContactForm />
            </div>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
}
